import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import MessagesGroup from './MessagesGroup';
import { usersRoles } from '../../../constants/general';
import { directConversationsSubTypes } from '../../../constants/conversations';
import { appPath } from '../../../constants';
import UsersService from '../../../services/UsersService';
import JobChatHeader from '../../common/ui/JobChatHeader';
import { Tooltip } from 'reactstrap';

const scrollOffset = 50;

export default class Conversation extends Component {
  static propTypes = {
    showHideChatIcon: PropTypes.bool,
    conversation: PropTypes.object,
    listData: PropTypes.array.isRequired,
    sendMessage: PropTypes.func.isRequired,
    loadMessagesHistory: PropTypes.func.isRequired,
    pushNewAdminMessageToList: PropTypes.func.isRequired,
    readConversationMessages: PropTypes.func.isRequired,
    updateUserConversationPreferences: PropTypes.func.isRequired
  };

  constructor () {
    super();
    this.endOfMessagesRef = null;
    this.messageToScrollRef = null;
    this.messagesContainerRef = null;
    this.state = {
      newMessage: '',
      messageToScrollId: null,
      sms: false,
      push: true,
      hideConversTooltip: false
    };
  }

  componentDidMount () {
    this.scrollToBottom();
    this.messagesContainerRef.addEventListener('scroll', this.handleScroll);
    this.props.readConversationMessages();
  }

  componentDidUpdate (prevProps) {
    const prevData = prevProps.conversation;
    const nextData = this.props.conversation;
    const conversationIsDifferent = prevData.conversation_id !== nextData.conversation_id;
    const hasNewMessages = !conversationIsDifferent && (prevData.messages.data.length !== nextData.messages.data.length);
    if (conversationIsDifferent || hasNewMessages) {
      this.scrollToBottom();
      this.props.readConversationMessages();
    }
  }

  componentWillUnmount () {
    this.messagesContainerRef.removeEventListener('scroll', this.handleScroll);
  }

  scrollToBottom = () => {
    if (this.endOfMessagesRef) {
      this.endOfMessagesRef.scrollIntoView({ behavior: 'smooth' });
    }
  };

  handleScroll = () => {
    const lastScrollTop = this.messagesContainerRef.scrollTop;
    if (lastScrollTop === 0) {
      this._loadMessages();
    }
  };

  /**
   * @description Load more messages if it is possible
   * @member _loadMessages
   */
  _loadMessages = () => {
    const { messages } = this.props.conversation;
    const { count, data } = messages;
    if (messages.data.length === 0) return; // in case start new direct conversation as employer on conversations and chats screen
    const canLoadMore = !count || data.length < count;
    if (canLoadMore) {
      this.setState({ messageToScrollId: data[0].id });
      this.props.loadMessagesHistory().then(() => {
        this.messageToScrollRef.scrollIntoView();
        this.messagesContainerRef.scrollTo({
          top: this.messagesContainerRef.scrollTop - scrollOffset // we need this additional offset of scroll to improve ux
        });
      });
    }
  };

  setMessageToScrollRef = messageElement => {
    if (messageElement) {
      this.messageToScrollRef = messageElement;
    }
  };

  setMessagesContainerRef = messagesContainerElement => {
    if (messagesContainerElement) {
      this.messagesContainerRef = messagesContainerElement;
    }
  };

  handleChangeMessage = e => {
    this.setState({
      newMessage: e.target.value
    });
  };

  handleKeySumbit = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  };

  handleChangeSMS = checked => {
    this.setState({
      sms: checked
    });
  };

  handleChangePush = checked => {
    this.setState({
      push: checked
    });
  };

  _sendMessage = e => {
    if (e) {
      e.preventDefault();
    }
    const { newMessage, sms, push } = this.state;
    if (!newMessage || (!sms && !push)) {
      return;
    }
    this.setState({
      newMessage: ''
    });
    this.props.sendMessage(newMessage, sms, push).then(data => {
      if (data) {
        this.props.pushNewAdminMessageToList(newMessage);
      }
    });
  };

  hideConversation = () => {
    const { conversation: { conversation_id, participant_id }, updateUserConversationPreferences } = this.props;
    updateUserConversationPreferences(conversation_id, participant_id, { is_deleted: true });
  };

  toggleTooltip = () => {
    this.setState(prevState => ({
      hideConversTooltip: !prevState.hideConversTooltip
    }));
  };

  render () {
    const { conversation, listData, showHideChatIcon } = this.props;
    const { title } = conversation;
    const { newMessage, sms, push, hideConversTooltip } = this.state;
    let messageToScrollId = this.state.messageToScrollId;
    if (!this.state.messageToScrollId && conversation.messages.data.length) {
      messageToScrollId = conversation.messages.data[0].id;
    }
    const sendingIsDisabled = !newMessage || (!sms && !push);
    const { user_id, roleid } = conversation.participants[0];
    const userRolePath = roleid === usersRoles.worker ? 'workers' : 'employers';
    const canHideConversation = showHideChatIcon && conversation.type !== directConversationsSubTypes.support;
    const isAdmin = UsersService.isAdmin();
    return (
      <React.Fragment>
        <header className='chat-header'>
          { isAdmin ? (
            <h3
              className='link-participant-name'
              onClick={() => window.open(`/${appPath}/${userRolePath}/${user_id}`)}
              title='Open Details'
            >{ title }</h3>
          ) : (
            <Fragment>
              { conversation.job_id ? <JobChatHeader conversation={conversation} /> : <h3 className='title'>{ title }</h3> }
            </Fragment>
          )}
          { canHideConversation && (
            <React.Fragment>
              <button
                id='hide-conversation-tooltip-target'
                className='button button-hide-conversation'
                onClick={this.hideConversation}
              >Hide</button>
              <Tooltip
                placement='top'
                isOpen={hideConversTooltip}
                target='hide-conversation-tooltip-target'
                toggle={this.toggleTooltip}
              >
                Hide Conversation
              </Tooltip>
            </React.Fragment>
          )}
        </header>
        <div className='messages-container' ref={element => { this.setMessagesContainerRef(element); }}>
          { listData.length ? (
            <React.Fragment>
              {listData.map((groupItem, index) => (
                <MessagesGroup
                  key={index}
                  conversation_id={conversation.conversation_id}
                  sender={groupItem.sender}
                  messages={groupItem.messages}
                  showDayDelimiter={groupItem.showDayDelimiter}
                  messageToScrollId={messageToScrollId}
                  setMessageToScrollRef={this.setMessageToScrollRef}
                />
              ))}
            </React.Fragment>
          ) : (
            <p>No messages yet...</p>
          )}
          <div ref={element => { this.endOfMessagesRef = element; }} />
        </div>
        <form
          onSubmit={this._sendMessage}
          className='chat-form'
        >
          <textarea
            className='input input-textarea input-send-message'
            rows='4'
            placeholder='Start typing something'
            value={newMessage}
            onChange={this.handleChangeMessage}
            onKeyPress={this.handleKeySumbit}
          />
          <div className='flex-wrapper mt-3'>
            { isAdmin && (
              <div className='notif-switchers'>
                <label className='switcher-wrapper'>
                  <Switch
                    onChange={this.handleChangeSMS}
                    checked={sms}
                    onColor='#44cbbd'
                    uncheckedIcon={false}
                    checkedIcon={false}
                    width={35}
                    height={19}
                  />
                  <span>SMS</span>
                </label>
                <label className='switcher-wrapper'>
                  <Switch
                    onChange={this.handleChangePush}
                    checked={push}
                    onColor='#44cbbd'
                    uncheckedIcon={false}
                    checkedIcon={false}
                    width={35}
                    height={19}
                  />
                  <span>In App</span>
                </label>
              </div>
            )}
            <button
              disabled={sendingIsDisabled}
              type='submit'
              className='button button-primary ml-sm-5 pl-4'
            >
              Send
            </button>
          </div>
        </form>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/messages/Conversation.js